import { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Box,
  MenuItem,
  Select,
  Stack,
  InputLabel,
  InputAdornment,
} from '@mui/material'
import { useLayout } from 'hooks/useLayout'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { useMutation } from '@tanstack/react-query'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { Page } from 'components/Shared/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import { TextField } from 'components/Shared/TextField'
import Typography from '@mui/material/Typography'
import { useApiClient } from 'hooks/useApiClient'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useSnackbar } from 'stores/useSnackbar'
import { object, string, mixed, date, number, array } from 'yup'
import { type ElementType } from 'react'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { HookableDateField } from 'components/Shared/HookableDateField'
import { HookableTimeField } from 'components/Shared/HookableTimeField'
import { isPresent } from '@jjvgaming/player-payback-library'
import type { RewardsCatalogSchedule, RewardsCatalogType } from 'types/api'
import { formatISO, parse } from 'date-fns'
import { shouldValidateEndTime, validateEndTime, daysOfWeek } from 'utils/util'
import { ScheduleRow } from 'pages/ClientManagement/LicensedEstablishments/LicensedEstablishmentNewPage/ScheduleRow'

const resolveComponent = (componentSource: unknown): ElementType => {
  if (
    componentSource &&
    typeof componentSource === 'object' &&
    'default' in componentSource
  ) {
    return (componentSource as { default: ElementType }).default
  }

  return componentSource as ElementType
}

interface FormData {
  name: string
  type: RewardsCatalogType
  startDate?: Date | null
  startTime?: string
  endDate?: Date | null
  endTime?: string
  scheduledRewardCatalog?: string
  schedules?: RewardsCatalogSchedule[]
}

const RewardCatalogSchema = object({
  name: string().required('Must enter a reward catalog name'),
  type: mixed<RewardsCatalogType>()
    .oneOf(['JJStoreCatalog', 'LocationCatalog'], 'Invalid catalog type')
    .required('Type is required'),
  scheduledRewardCatalog: string(),
  startDate: date()
    .nullable()
    .typeError('This date is invalid')
    .transform((currentValue, originalValue, context) => {
      if (context.isType(currentValue)) {
        return currentValue
      }
      if (originalValue === '') {
        return null
      }
      if (originalValue === 'MM/DD/YYYY') {
        return null
      }
      try {
        parse(originalValue, 'MM/DD/YYYY', new Date())
      } catch (error) {
        return new Date('')
      }
      return currentValue
    }),
  startTime: string().when('startDate', {
    is: (startDate: Date | null) => startDate !== null,
    then: () =>
      string().matches(
        /^(0\d|1[0-2]):[0-5]\d [APap][mM]$/,
        'Invalid time format. Please enter a valid time.'
      ),
  }),
  endDate: date()
    .nullable()
    .typeError('This date is invalid')
    .transform((currentValue, originalValue, context) => {
      if (context.isType(currentValue)) {
        return currentValue
      }
      if (originalValue === '') {
        return null
      }
      if (originalValue === 'MM/DD/YYYY') {
        return null
      }
      try {
        parse(originalValue, 'MM/DD/YYYY', new Date())
      } catch (error) {
        return new Date('')
      }
      return currentValue
    })
    .when(
      ['scheduledRewardCatalog', 'startDate'],
      ([scheduledRewardCatalog, startDate], schema) => {
        if (
          scheduledRewardCatalog === 'Yes' &&
          startDate instanceof Date &&
          isFinite(+startDate)
        ) {
          return schema.min(
            startDate,
            'End date should not be before start date'
          )
        }
        return schema
      }
    ),
  endTime: string().when(
    ['startDate', 'startTime', 'endDate'],
    ([startDate, startTime, endDate], schema) => {
      return schema.test({
        name: 'endTime',
        message: 'This field is required',
        test: (value) => {
          const timeRegex = /^(0?[1-9]|1[0-2]):[0-5]\d (AM|PM)$/
          if (
            value &&
            endDate instanceof Date &&
            isFinite(+endDate) &&
            !timeRegex.test(value)
          ) {
            return false
          }

          if (endDate instanceof Date && value?.toString().length === 0) {
            return false
          }

          if (value && endDate instanceof Date && isFinite(+endDate)) {
            shouldValidateEndTime(startDate, startTime, endDate, value) &&
              validateEndTime(startDate, startTime, endDate, value)
          }

          return true
        },
      })
    }
  ),
  schedules: array().of(
    object({
      id: number().nullable().optional(),
      rewardCatalogId: number().optional(),
      dayName: string().optional(),
      opening: string().optional(),
      closing: string().optional(),
    })
  ),
})

export const RewardCatalogsNewPage = () => {
  const { post } = useApiClient()
  const navigate = useNavigate()
  const setMessage = useSnackbar((state) => state.setMessage)
  const { isMobile } = useLayout()
  const [selectedScheduledRewardCatalog, setSelectedScheduledRewardCatalog] =
    useState('No')

  const formMethods = useForm({
    resolver: yupResolver(RewardCatalogSchema),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods

  const FormFieldErrorMessageComponent = resolveComponent(FormFieldErrorMessage)
  const HookableDateFieldComponent = resolveComponent(HookableDateField)
  const HookableTimeFieldComponent = resolveComponent(HookableTimeField)
  const ScheduleRowComponent = resolveComponent(ScheduleRow)
  const EventIconComponent = resolveComponent(EventIcon)
  const AccessTimeIconComponent = resolveComponent(AccessTimeIcon)

  const mutate = useMutation({
    mutationKey: ['rewards/catalog/create'],
    mutationFn: async ({
      name,
      type,
      startDate,
      startTime,
      endDate,
      endTime,
      schedules,
    }: FormData) => {
      const rewardCatalogSchedules: RewardsCatalogSchedule[] = []
      schedules?.forEach((schedule) => {
        if (schedule.opening && schedule.closing) {
          rewardCatalogSchedules.push({
            ...schedule,
          })
        }
      })
      const response = await post('/admin/rewards-catalogs', {
        params: { header: undefined },
        body: {
          name,
          type,
          startDate: startDate
            ? formatISO(parse(startTime ?? '', 'hh:mm a', startDate))
            : null,
          endDate: endDate
            ? formatISO(parse(endTime ?? '', 'hh:mm a', endDate))
            : null,
          rewardsCatalogSchedules: rewardCatalogSchedules,
        },
      })
      return response
    },
    onSuccess: (response) => {
      navigate(`/Rewards/RewardCatalogs/${response.data?.id}`)
      setMessage('Reward Catalog was successfully created.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
    onError: () => {
      setMessage('Error creating reward catalog', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  if (mutate.isPending) {
    return <ActivityIndicator />
  }

  if (mutate.isError) {
    return <h1>An error occured.</h1>
  }

  const createRewardsCatalogWrapper = handleSubmit((data) => {
    mutate.mutate(data)
  })

  return (
    <Page
      header={
        <PageHeader
          title={'Create a New Reward Catalog'}
          subtitle={`You must fill out every field in the form before clicking the submit button.\nIncomplete submissions cannot be processed.`}
          backText="Reward Catalogs"
          backPath="/Rewards/RewardCatalogs"
          isSecondary={true}
        ></PageHeader>
      }
    >
      <FormProvider {...formMethods}>
        <form onSubmit={createRewardsCatalogWrapper}>
          <TextField
            label="Reward Catalog Name"
            placeholder="Enter reward catalog name"
            id="new-rewards-catalog-name-text-box"
            required
            error={isPresent(errors.name)}
            {...register('name', { required: true })}
          />
          <Typography fontWeight="bold" className="pt-4 pb-2">
            Reward Catalog Type <span style={{ color: '#CC2027' }}>*</span>
          </Typography>
          <Select
            className="w-[200px]"
            id="type"
            {...register('type')}
            required={true}
            variant="outlined"
            defaultValue="LocationCatalog"
          >
            <MenuItem value="LocationCatalog">Location Catalog</MenuItem>
            <MenuItem value="JJStoreCatalog">J&J Store Catalog</MenuItem>
          </Select>
          {errors.name?.message && (
            <FormFieldErrorMessageComponent message={errors.name.message} />
          )}
          <Stack direction="column" gap={1} width={'500px'} paddingTop={2}>
            <InputLabel
              htmlFor={'scheduledBoolean'}
              sx={isMobile ? { fontSize: '16px' } : {}}
            >
              Do you want to schedule this Reward Catalog?
            </InputLabel>
            <Select
              {...register('scheduledRewardCatalog', { required: true })}
              inputProps={{ id: 'scheduledBoolean' }}
              data-testid="scheduledBoolean"
              error={isPresent(errors.scheduledRewardCatalog)}
              defaultValue="No"
              value={selectedScheduledRewardCatalog}
              onChange={(e) =>
                setSelectedScheduledRewardCatalog(e.target.value)
              }
            >
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
            </Select>
            {errors.scheduledRewardCatalog?.message && (
              <FormFieldErrorMessageComponent
                message={errors.scheduledRewardCatalog.message}
              />
            )}
          </Stack>
          {selectedScheduledRewardCatalog === 'Yes' && (
            <div>
              <Typography variant="body-2">
                To keep the reward catalog active, don&apos;t select an end date
                and time.
              </Typography>
              <div>
                <Stack direction={'row'} spacing={2} marginTop={'15px'}>
                  <div style={{ flex: 1, width: '100%' }}>
                    <InputLabel sx={isMobile ? { fontSize: '16px' } : {}}>
                      Start Date
                    </InputLabel>
                    <HookableDateFieldComponent
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startDate),
                        },
                      }}
                      {...register('startDate')}
                    />
                    {errors.startDate?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.startDate.message}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1, width: '100%' }}>
                    <InputLabel sx={isMobile ? { fontSize: '16px' } : {}}>
                      Start Time
                    </InputLabel>
                    <HookableTimeFieldComponent
                      {...register('startTime')}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startTime),
                        },
                      }}
                      sx={{ width: '100%' }}
                    />
                    {errors.startTime?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.startTime.message}
                      />
                    )}
                  </div>
                </Stack>
              </div>
              <Stack direction={'row'} spacing={2} marginTop={'15px'}>
                <div style={{ flex: 1, width: '100%' }}>
                  <InputLabel sx={isMobile ? { fontSize: '16px' } : {}}>
                    End Date
                  </InputLabel>
                  <HookableDateFieldComponent
                    sx={{ width: '100%' }}
                    slotProps={{
                      textField: {
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <EventIconComponent />
                            </InputAdornment>
                          ),
                        },
                        error: isPresent(errors.endDate),
                      },
                    }}
                    {...register('endDate')}
                  />
                  {errors.endDate?.message && (
                    <FormFieldErrorMessageComponent
                      message={errors.endDate.message}
                    />
                  )}
                </div>
                <div style={{ flex: 1, width: '100%' }}>
                  <InputLabel sx={isMobile ? { fontSize: '16px' } : {}}>
                    End Time
                  </InputLabel>
                  <HookableTimeFieldComponent
                    {...register('endTime')}
                    slotProps={{
                      textField: {
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <AccessTimeIconComponent />
                            </InputAdornment>
                          ),
                        },
                        error: isPresent(errors.endTime),
                      },
                    }}
                    sx={{ width: '100%' }}
                  />
                  {errors.endTime?.message && (
                    <FormFieldErrorMessageComponent
                      message={errors.endTime.message}
                    />
                  )}
                </div>
              </Stack>
              <Box className="pt-8">
                <Typography variant="subtitle-1" component={'p'}>
                  Reward Catalog Schedule
                </Typography>
                <Typography variant="body-3">
                  {`Select the days you'd like catalog items to be available to patrons`}
                </Typography>
                <Stack spacing={2} mt="20px">
                  {daysOfWeek.map((day, index) => {
                    return (
                      <Box
                        key={day}
                        style={{ height: '85px' }}
                        height={'85'}
                        className="flex flex-col justify-center"
                      >
                        <ScheduleRowComponent
                          day={day}
                          index={index}
                          key={day}
                          defaultValues={{
                            opening: '',
                            closing: '',
                            patronCheckInPointBonusMultiplier: 0,
                          }}
                          openLabel="Enabled"
                          closedLabel="Disabled"
                        />
                      </Box>
                    )
                  })}
                </Stack>
              </Box>
            </div>
          )}
          <Box
            sx={{
              pt: '24px',
              display: 'flex',
              flexDirection: 'row',
              alignContent: { xs: 'between' },
              columnGap: { xs: '16px', sm: '24px' },
            }}
          >
            <Button
              variant="outlined"
              sx={{ width: { xs: '171px', sm: '104px' } }}
              onClick={() => {
                navigate('/Rewards/RewardCatalogs')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              sx={{ width: { xs: '171px', sm: '104px' } }}
            >
              Submit
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Page>
  )
}
